import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import type {
  SearchCatalogProperty,
  SearchOverview,
  SearchProvider,
} from '../../../api/src/search-metrics/search-metrics.types';
import { api } from '../api/client';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { Box, Stack, Typography } from '../components/mui-system';
import { sourceNames } from './search-metrics-presentation';

export function MetricsConnectionsDialog({
  workspaceId,
  overview,
  close,
  changed,
}: {
  workspaceId: string;
  overview: SearchOverview;
  close(): void;
  changed(): Promise<void>;
}) {
  const [properties, setProperties] = useState<SearchCatalogProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const canEdit = useCanEdit('metrics.edit');
  useEffect(() => {
    let active = true;
    api
      .searchProperties(workspaceId)
      .then((rows) => {
        if (active) setProperties(rows);
      })
      .catch((e) => {
        if (active) setError(String(e.message));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId]);
  async function perform(action: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await action();
      await changed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось обновить подключение');
    } finally {
      setBusy(false);
    }
  }
  async function connect(provider: SearchProvider) {
    await perform(async () => {
      const result = await api.connectSearch(workspaceId, provider);
      window.location.assign(result.url);
    });
  }
  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="lg">
      <DialogTitle>Подключения и сайты</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {(loading || busy) && <LinearProgress />}
          <Typography color="text.secondary">
            Подключите аккаунты и выберите ресурсы для статистики. Доступ и выбранные сайты общие
            для этого workspace.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            {(['google', 'yandex'] as const).map((provider) => (
              <Box key={provider} flex={1}>
                <EditorOnly permissions="metrics.settings">
                  <Button
                    variant="outlined"
                    fullWidth
                    disabled={busy || !overview.configuration[provider].configured}
                    onClick={() => void connect(provider)}
                  >
                    Добавить {sourceNames[provider]}-аккаунт
                  </Button>
                </EditorOnly>
                {!overview.configuration[provider].configured && (
                  <Typography fontSize={12} color="text.secondary" mt={1}>
                    Подключение {sourceNames[provider]} ещё не настроено.{' '}
                    <Link href="/docs/search-metrics">Инструкция настройки</Link>
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
          {overview.connections.map((connection) => (
            <Box
              key={connection.id}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                gap={1}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
              >
                <Box>
                  <Typography fontWeight={700}>{connection.label}</Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {sourceNames[connection.provider]} ·{' '}
                    {connection.connected ? 'Подключён' : 'Отключён'}
                  </Typography>
                </Box>
                <EditorOnly any permissions={['metrics.run', 'metrics.delete']}>
                  <Stack direction="row" gap={1}>
                    {connection.connected && (
                      <>
                        <EditorOnly permissions="metrics.run">
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() =>
                              void perform(async () =>
                                setProperties(
                                  await api.refreshSearchConnection(workspaceId, connection.id),
                                ),
                              )
                            }
                          >
                            Обновить список сайтов
                          </Button>
                        </EditorOnly>
                        <EditorOnly permissions="metrics.delete">
                          <Button
                            size="small"
                            color="error"
                            disabled={busy}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Отключить ${connection.label} для workspace? Сохранённая история останется.`,
                                )
                              )
                                void perform(async () => {
                                  await api.disconnectSearch(workspaceId, connection.id);
                                  setProperties(await api.searchProperties(workspaceId));
                                });
                            }}
                          >
                            Отключить
                          </Button>
                        </EditorOnly>
                      </>
                    )}
                  </Stack>
                </EditorOnly>
              </Stack>
              {connection.error && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {connection.error}
                </Alert>
              )}
            </Box>
          ))}
          <Divider />
          <Typography variant="h6">Сайты в Метриках</Typography>
          <Typography fontSize={13} color="text.secondary">
            Для каждого сайта можно выбрать по одному ресурсу Google и Яндекса. Выбор другого
            ресурса того же сайта заменяет предыдущий. Это исключает двойной подсчёт.
          </Typography>
          <TextField
            size="small"
            label="Найти ресурс"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!loading && !properties.length ? (
            <Alert severity="info">
              После подключения аккаунта здесь появятся сайты, к которым у него есть доступ.
            </Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 410 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Выбран</TableCell>
                    <TableCell>Сайт / ресурс</TableCell>
                    <TableCell>Источник</TableCell>
                    <TableCell>Аккаунты</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties
                    .filter((p) =>
                      `${p.siteKey} ${p.label}`.toLowerCase().includes(search.toLowerCase()),
                    )
                    .map((property) => (
                      <TableRow key={property.id}>
                        <TableCell>
                          <Checkbox
                            checked={Boolean(property.selected)}
                            disabled={
                              !canEdit ||
                              busy ||
                              (!property.selected && !property.connectionIds.length)
                            }
                            slotProps={{
                              input: {
                                'aria-label': `Выбрать ${sourceNames[property.provider]} ${property.label}`,
                              },
                            }}
                            onChange={(_, checked) =>
                              void perform(async () =>
                                setProperties(
                                  await api.selectSearchProperty(workspaceId, property.id, checked),
                                ),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600} fontSize={13}>
                            {property.siteKey}
                          </Typography>
                          <Typography
                            fontSize={12}
                            color="text.secondary"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            {property.label}
                          </Typography>
                        </TableCell>
                        <TableCell>{sourceNames[property.provider]}</TableCell>
                        <TableCell>
                          {property.connectionIds
                            .map((id) => overview.connections.find((c) => c.id === id)?.label ?? id)
                            .join(', ') || 'Нет доступа'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <Box px={3} pb={1}>
        <Typography variant="body2" color="text.secondary">
          После выбора сайта доступная история загружается автоматически. Свежие данные обновляются
          ежедневно.
        </Typography>
      </Box>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Готово
        </Button>
      </DialogActions>
    </Dialog>
  );
}
